import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { IconType } from 'react-icons';
import {
  SiAsana,
  SiClickup,
  SiGithub,
  SiGitlab,
  SiJira,
  SiLinear,
  SiNotion,
  SiSlack,
} from 'react-icons/si';
import { VscAzureDevops } from 'react-icons/vsc';
import { ArrowUp, ChevronDown, ExternalLink, Image, Share2, X } from 'lucide-react';
import { RichTextArea } from '../../components/RichTextArea';
import { richTextHtmlToMarkdown } from '../../utils/richTextHtmlToMarkdown';
import { sanitizeRichHtml } from '../../utils/sanitizeRichHtml';

type Attachment = { id: string; src: string; name: string };

type PostedComment = {
  id: string;
  bodyHtml: string;
  attachments: Attachment[];
  markdown: string;
  at: number;
};

function uid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const SEND_INTEGRATIONS: readonly {
  id: string;
  name: string;
  Icon: IconType;
  /** Brand tint for the SVG (`currentColor` / fill). */
  iconColor: string;
  /** Light chip behind dark or monochrome marks so they stay legible on dark menus. */
  iconTileBg?: string;
}[] = [
  { id: 'slack', name: 'Slack', Icon: SiSlack, iconColor: '#4A154B' },
  { id: 'jira', name: 'Jira', Icon: SiJira, iconColor: '#0052CC' },
  { id: 'linear', name: 'Linear', Icon: SiLinear, iconColor: '#5E6AD2' },
  { id: 'clickup', name: 'ClickUp', Icon: SiClickup, iconColor: '#7B68EE' },
  { id: 'notion', name: 'Notion', Icon: SiNotion, iconColor: '#37352f', iconTileBg: '#f7f6f3' },
  { id: 'github', name: 'GitHub', Icon: SiGithub, iconColor: '#24292f', iconTileBg: '#f6f8fa' },
  { id: 'azure', name: 'Azure DevOps', Icon: VscAzureDevops, iconColor: '#0078D4' },
  { id: 'asana', name: 'Asana', Icon: SiAsana, iconColor: '#F06A6A' },
  { id: 'gitlab', name: 'GitLab', Icon: SiGitlab, iconColor: '#FC6D26' },
];

function SendToAppMenu() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Send to app or connected tool"
        onClick={() => setOpen((v) => !v)}
        className="flex max-w-[min(100%,12rem)] items-center gap-1.5 rounded border border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-1 text-[11px] font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--border)]"
      >
        <ExternalLink className="h-3 w-3 shrink-0 text-[var(--accent)]" strokeWidth={2} aria-hidden />
        <span className="hidden min-w-0 truncate sm:inline">Send to app</span>
        <span className="truncate sm:hidden">Send</span>
        <ChevronDown
          className={`h-3 w-3 shrink-0 text-[var(--text-muted)] transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={2}
          aria-hidden
        />
      </button>
      {open ? (
        <div
          role="menu"
          aria-label="Connect integrations"
          className="absolute right-0 top-[calc(100%+6px)] z-[200] w-[min(16rem,calc(100vw-1.5rem))] overflow-hidden rounded-md border border-[var(--border)] bg-[var(--bg-sidebar)] py-0.5 shadow-lg ring-1 ring-[var(--menu-ring)]"
        >
          {SEND_INTEGRATIONS.map((app) => {
            const Icon = app.Icon;
            return (
              <button
                key={app.id}
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2.5 px-2.5 py-1.5 text-left text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-elevated)]"
                onClick={() => setOpen(false)}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded border border-[var(--border-subtle)] shadow-sm ${
                    app.iconTileBg ? '' : 'bg-[var(--bg-elevated)]'
                  }`}
                  style={app.iconTileBg ? { backgroundColor: app.iconTileBg } : undefined}
                  aria-hidden
                >
                  <Icon className="h-4 w-4 shrink-0" color={app.iconColor} aria-hidden />
                </span>
                <span className="min-w-0 truncate">
                  Connect <span className="font-semibold">{app.name}</span>
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Rich draft + comments for future sharing (local only). HTML is edited WYSIWYG;
 * markdown is derived for export. One Cursor-style composer: draft + comment in one card.
 */
export const SessionSharePanel: React.FC<{
  compact?: boolean;
  mobileDock?: boolean;
  /** Mobile dock only: collapse control in the Sharing header (parent hides the dock). */
  onRequestCollapse?: () => void;
}> = ({ compact, mobileDock, onRequestCollapse }) => {
  const dense = Boolean(compact);
  const docked = Boolean(mobileDock);
  const [draftHtml, setDraftHtml] = useState('');
  const [commentHtml, setCommentHtml] = useState('');
  const [commentAttachments, setCommentAttachments] = useState<Attachment[]>([]);
  const [posted, setPosted] = useState<PostedComment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formId = useId();

  const onPickFiles = useCallback((files: FileList | null) => {
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      const reader = new FileReader();
      reader.onload = () => {
        const src = typeof reader.result === 'string' ? reader.result : '';
        if (!src) return;
        setCommentAttachments((prev) => [...prev, { id: uid(), src, name: file.name }]);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const sendComment = useCallback(() => {
    const text = commentHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (!text && commentAttachments.length === 0) return;

    const imgMd = commentAttachments.map((a) => `![${a.name}](${a.src})`).join('\n\n');
    const bodyMd = richTextHtmlToMarkdown(commentHtml);
    const markdown = [imgMd, bodyMd].filter(Boolean).join('\n\n');

    setPosted((prev) => [
      {
        id: uid(),
        bodyHtml: sanitizeRichHtml(commentHtml),
        attachments: [...commentAttachments],
        markdown,
        at: Date.now(),
      },
      ...prev,
    ]);
    setCommentHtml('');
    setCommentAttachments([]);
  }, [commentHtml, commentAttachments]);

  const composerCard =
    'flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-editor)] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]';
  /** Fixed-ish height so the draft `fillHeight` editor still lays out inside the bottom dock. */
  const composerInDock =
    'flex h-[min(260px,40vh)] min-h-[200px] flex-col overflow-hidden rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-editor)] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]';

  return (
    <aside
      className={
        dense
          ? docked
            ? 'flex min-h-0 min-w-0 flex-col bg-[var(--bg-sidebar)] px-2 pb-2 pt-1'
            : 'flex h-full min-h-0 min-w-0 flex-col border-t border-[var(--border-subtle)] bg-[var(--bg-sidebar)] p-3'
          : 'flex h-full min-h-0 min-w-0 flex-col overflow-x-hidden bg-[var(--bg-sidebar)]'
      }
    >
      <div
        className={
          dense
            ? docked
              ? 'flex shrink-0 items-center justify-between gap-2 border-b border-[var(--border-subtle)]/70 pb-1.5 pl-0.5 pr-0.5'
              : 'flex shrink-0 items-center justify-between gap-2 border-b border-[var(--border-subtle)] pb-2'
            : 'flex shrink-0 items-center justify-between gap-2 border-b border-[var(--border-subtle)] px-3 py-2.5'
        }
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Share2 className="h-3.5 w-3.5 shrink-0 text-[var(--text-muted)]" aria-hidden />
          <h2 className="min-w-0 truncate text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
            Sharing
          </h2>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <SendToAppMenu />
          {docked && onRequestCollapse ? (
            <button
              type="button"
              className="flex shrink-0 rounded p-1 text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-elevated)]/70 hover:text-[var(--text-primary)]"
              aria-expanded
              aria-label="Collapse sharing"
              title="Collapse sharing"
              onClick={onRequestCollapse}
            >
              <ChevronDown className="h-4 w-4" strokeWidth={2} aria-hidden />
            </button>
          ) : null}
        </div>
      </div>

      <div
        className={
          dense
            ? docked
              ? 'mt-1 flex min-h-0 flex-col gap-2'
              : 'mt-2 flex min-h-0 flex-1 flex-col gap-2'
            : 'flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden p-3'
        }
      >
        {posted.length > 0 && (
          <section className="shrink-0 space-y-2" aria-label="Posted comments">
            {posted.map((c) => (
              <article
                key={c.id}
                className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-panel)]/80 px-3 py-2 text-sm text-[var(--text-primary)]"
              >
                {c.attachments.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {c.attachments.map((a) => (
                      <img
                        key={a.id}
                        src={a.src}
                        alt={a.name}
                        className="max-h-24 max-w-full rounded-md border border-[var(--border-subtle)] object-contain"
                      />
                    ))}
                  </div>
                )}
                <div
                  className="prose-share prose-sm max-w-none text-[var(--text-primary)] [&_a]:text-[var(--link)] [&_li]:my-0.5 [&_p]:my-1 [&_ul]:my-1"
                  dangerouslySetInnerHTML={{ __html: c.bodyHtml || '<p class="text-[var(--text-muted)]">(empty)</p>' }}
                />
              </article>
            ))}
          </section>
        )}

        <div
          className={`${docked && dense ? composerInDock : `${composerCard} min-h-[220px]`}`}
          aria-label="Share composer"
        >
          <div className="flex shrink-0 items-baseline justify-between gap-2 border-b border-[var(--border-subtle)]/60 px-3 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">Draft</span>
            <span className="text-[10px] text-[var(--text-muted)]">Select text to format</span>
          </div>

          <div className="min-h-0 flex-1 px-1">
            <RichTextArea
              id={`${formId}-draft`}
              aria-label="Share draft"
              value={draftHtml}
              onChange={setDraftHtml}
              placeholder="Write what you want to share…"
              variant="document"
              compact={dense}
              frameless={true}
              fillHeight={true}
            />
          </div>

          <div className="shrink-0 border-t border-[var(--border-subtle)]/50" aria-label="Write a comment">
            {commentAttachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 border-b border-[var(--border-subtle)]/40 px-2 py-1.5">
                {commentAttachments.map((a) => (
                  <div key={a.id} className="relative inline-block">
                    <img
                      src={a.src}
                      alt=""
                      className="h-9 w-9 rounded-md border border-[var(--border-subtle)] object-cover"
                    />
                    <button
                      type="button"
                      className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--bg-elevated)] text-[var(--text-primary)] shadow ring-1 ring-[var(--border-subtle)] hover:bg-[var(--border)]"
                      aria-label={`Remove ${a.name}`}
                      onClick={() => setCommentAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <RichTextArea
              id={`${formId}-comment`}
              aria-label="Comment"
              value={commentHtml}
              onChange={setCommentHtml}
              placeholder="Write a comment…"
              variant="comment"
              compact={dense}
              frameless={true}
            />
            <div className="flex items-center justify-between gap-1.5 px-1.5 pb-1.5 pt-0">
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="sr-only"
                  aria-label="Attach images"
                  onChange={(e) => {
                    onPickFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  className="rounded-md p-1.5 text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-elevated)]/70 hover:text-[var(--text-primary)]"
                  aria-label="Attach image"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Image className="h-3.5 w-3.5" strokeWidth={1.75} />
                </button>
              </div>
              <button
                type="button"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-[var(--on-accent)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-35"
                aria-label="Send comment"
                disabled={
                  !commentHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() && commentAttachments.length === 0
                }
                onClick={sendComment}
              >
                <ArrowUp className="h-3.5 w-3.5" strokeWidth={2.25} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};
